import { DataProvider, GetListParams, SortPayload } from 'react-admin';
import { removeEmptyStrings } from './dataProviders';
import { httpClient } from './httpClient';
import { supabaseStorage, supabase } from '../lib/supabaseClient'
import { AuthSession } from '@supabase/supabase-js'
import { decode } from 'base64-arraybuffer'
import { Buffer } from "buffer";
import { v4 as uuidv4 } from 'uuid';
import { IDataProviderConfig, defaultPrimaryKeys, defaultSchema } from '@raphiniert/ra-data-postgrest';
import postgrestRestProvider from '@raphiniert/ra-data-postgrest';
import {
  REACT_APP_SUPABASE_URL,
  REACT_APP_SUPABASE_ANON_KEY,
} from '../lib/constants';
import { revalidateObituary, revalidateObituaryForFuneral } from './revalidateObituary';
import { ensureContractForFuneral } from './ensureContractForFuneral';


import { supabaseDataProvider } from 'ra-supabase-core';

// const config: IDataProviderConfig = {
//   apiUrl: process.env.REACT_APP_API_URL + 'rest/v1',
//   httpClient: httpClient,
//   defaultListOp: 'eq',
//   primaryKeys: defaultPrimaryKeys,
//   schema: defaultSchema
// }

// Accept: application/vnd.pgrst.object+json
// const postgrestDataProvider = postgrestRestProvider(config);

const baseSupabaseDataProvider = supabaseDataProvider({
  instanceUrl: REACT_APP_SUPABASE_URL as string,
  apiKey: REACT_APP_SUPABASE_ANON_KEY as string,
  supabaseClient: supabase
});


// eslint-disable-next-line @typescript-eslint/no-explicit-any

export const pgrestDataProvider: DataProvider = {
  ...baseSupabaseDataProvider,
  
  getList: async (resource, params) => {
    // Handle analytics schema views using RPC
    if (resource.startsWith('analytics.')) {
      const viewName = resource.split('.')[1];
      const offset = (params.pagination.page - 1) * params.pagination.perPage;
      const limit = params.pagination.perPage;
      
      // Use raw SQL query via RPC
      const { data, error } = await supabase.rpc('query_analytics_view', {
        view_name: viewName,
        order_by: params.sort.field,
        order_dir: params.sort.order,
        page_limit: limit,
        page_offset: offset
      });
      
      if (error) throw error;

      // React-Admin requires each row to have an 'id' field
      const rows = (data || []).map((row: any, idx: number) => ({
        id: row.id || row.lead_id || row.week_start || idx,
        ...row,
      }));

      return {
        data: rows,
        total: rows.length,
      };
    }

    if('q' in params.filter && resource == 'funerals'){
      resource = 'rpc/search_clients';
      delete Object.assign(params.filter, {['keyword@']: `${params.filter['q']}:*` })['q'];
    }

    if(resource == 'events'){
      params.pagination.perPage = 10;
      Object.assign(params.filter, {['record_id']: params.filter.payload.data.id})
      delete params.filter.payload;
      
    }
    
    
    if(resource == 'funerals'){
      resource = 'funerals_searchable';
      
     const newSort : SortPayload = {
      field: params.sort["field"],
      order: params.sort["order"].concat('.nullslast') as 'ASC' | 'DESC'
     }

     params.sort = newSort;

    }

    if(resource == 'clients'){
      resource='clients_unaccent';  
    }
    
    if(resource == 'contracts'){
      resource='contracts_list';
    }

    return baseSupabaseDataProvider.getList(resource, params);
  },
  getOne: (resource, params) => {
    return baseSupabaseDataProvider.getOne(resource, params);
  },
  update: async (resource, params) => {
    if(resource == 'funerals'){
      delete params.data.list_date;
    }

    if('files' in params.data && resource == 'funerals'){
      const file = params.data.files;
      const response = await fetch(file.src);
      const blob = await(response.blob());
      const filePath = `funerals/${params.data.id}/${file.title}`
  
      const { error: uploadError } = await supabaseStorage.storage
        .from('attachments')
        .upload(filePath, blob, {
          upsert: true,
          cacheControl: '3600'
        })
  
      if (uploadError) {
        console.error('Storage upload error:', uploadError);
        throw uploadError
      }

      const { error: updateError } = await supabase.from('attachments').upsert({
        path: filePath,
        title: file.title,
        funeral_id: params.data.id,
        document_type: file.document_type || null
      })

      if (updateError) {
        throw updateError
      }

      // If this is an SP3 file, update the sp3_received flag on the funeral record
      if (file.document_type === 'SP3') {
        const { error: funeralUpdateError } = await supabase
          .from('funerals')
          .update({ sp3_received: true })
          .eq('id', params.data.id);
          
        if (funeralUpdateError) {
          console.error('Error updating sp3_received flag:', funeralUpdateError);
        }
      }

      return {
        data: {
          ...params.previousData,
          id: params.id,
        }
      };
    }

    if('client_image' in params.data && resource=='clients'){

      const image = params.data.client_image;
      const imageBase64Str = image.replace(/^.+,/, '');
      const buf = Buffer.from(imageBase64Str, 'base64');

      // Use funeral_id for the path if provided, otherwise fall back to client id
      const funeralId = params.data.funeral_id;
      const filePath = `funerals/${funeralId}/${uuidv4()}`;
  
      const { error: uploadError } = await supabaseStorage.storage
        .from('memorial-images')
        .upload(filePath, buf, {
          contentType: 'image/png',
          upsert: true,
          cacheControl: '3600'
        })

  
      if (uploadError) {
        console.log('storage error', uploadError)
        throw uploadError
      }

      const { error: updateError } = await supabase.from('clients').update({
        image_path: filePath,
      })
      .eq('id', params.data.id);

      if (updateError) {
        console.log('clients table error', updateError)
        throw updateError
      }

      // The memorial photo shows on the public obituary page, so refresh its cache too.
      void revalidateObituaryForFuneral(funeralId);

      // Return early after successful image upload - no need to call base provider
      return {
        data: {
          ...params.previousData,
          id: params.id,
          image_path: filePath,
        }
      };
    }

    params.data = removeEmptyStrings(params.data);
    
    // Fix boolean values being converted to strings
    if (resource === 'funerals') {
      const booleanFields = [
        'public_curator', 'coroner', 'is_live', 'web_sticky', 'streaming', 'aftercare',
        'foreign_social_security', 'children_after_1958', 'children_provided_after_1958',
        'visible_to_partners'
      ];
      
      booleanFields.forEach(field => {
        if (field in params.data) {
          // Convert string booleans back to actual booleans
          if (params.data[field] === 'true') params.data[field] = true;
          if (params.data[field] === 'false') params.data[field] = false;
        }
      });
    }
    
    const result = await baseSupabaseDataProvider.update(resource, params);

    // Revalidate cleo-web obituary cache when funeral is updated (publishing an
    // obituary must not wait out cleo-web's hour-long ISR window).
    if (resource === 'funerals' && result.data?.slug) {
      void revalidateObituary(result.data.slug as string);
    }

    return result;
  },
  getMany: (resource, params) => {
    return baseSupabaseDataProvider.getMany(resource, params);
  },

  updateMany: (resource, params) => {
    params.data = removeEmptyStrings(params.data);
    return baseSupabaseDataProvider.updateMany(resource, params);
  },
  create: async (resource, params) => {
    params.data = removeEmptyStrings(params.data);
    
    // If creating a funeral, also create a related quote
    if (resource === 'funerals') {
      // Form-only field that drives the related quote's deal_type; not a funerals column
      const dealType = params.data._deal_type === 'pre_need' ? 'pre_need' : 'at_need';
      delete params.data._deal_type;

      const result = await baseSupabaseDataProvider.create(resource, params);
      
      // Extract information for quote creation
      const funeralData = result.data;
      const deceasedClientId = funeralData.deceased_client_id;
      const primaryClientId = funeralData.primary_client_id;
      
      // Get client information for quote
      let deceasedClient = null;
      let primaryClient = null;
      
      if (deceasedClientId) {
        const deceasedResult = await baseSupabaseDataProvider.getOne('clients', { id: deceasedClientId });
        deceasedClient = deceasedResult.data;
      }
      
      if (primaryClientId) {
        const primaryResult = await baseSupabaseDataProvider.getOne('clients', { id: primaryClientId });
        primaryClient = primaryResult.data;
      }
      
      // Always create a quote for this funeral (1:1 relationship)
      try {
        // Create the quote with information from the funeral
        // Note: client_id can be null if no primary client is set yet
        const quoteData = {
          client_id: primaryClientId || null,
          deceased_first_name: deceasedClient?.first_name || '',
          deceased_last_name: deceasedClient?.last_name || '',
          email: primaryClient?.email || '',
          phone_number: primaryClient?.phone_number || '',
          contact_name: primaryClient ? `${primaryClient.first_name || ''} ${primaryClient.last_name || ''}`.trim() : '',
          quoted_price: 1900, // Default price
          language: funeralData.language || 'en',
          qualified: true, // Since we're creating from admin, it's already qualified
          deal_type: dealType,
          funeral_id: funeralData.id,
        };
        
        // Remove any fields that are null to avoid database type errors
        if (!quoteData.client_id) {
          delete quoteData.client_id;
        }
        
        const quoteResult = await baseSupabaseDataProvider.create('quotes', { data: quoteData });
        console.log('Auto-created quote for funeral:', funeralData.id, 'Quote ID:', quoteResult.data.id, 'Client ID:', primaryClientId || 'null');
      } catch (error) {
        console.error('Failed to auto-create quote for funeral:', funeralData.id, error);
        // Don't fail the funeral creation if quote creation fails
      }

      // Auto-create the funeral's opening contract, priced from its region.
      //
      // regionSource is 'explicit' because the region came from the form's
      // required radio -- an affirmative choice even when it is left on Quebec.
      // That is the difference from the aura path, which inherits region from a
      // quote and must skip when the quote had none.
      //
      // Same try/catch shape as the quote above, for the same reason: a funeral
      // must still be created when its side effects fail.
      try {
        await ensureContractForFuneral(funeralData.id, {
          dealType,
          regionSource: 'explicit',
        });
      } catch (error) {
        console.error('Failed to auto-create contract for funeral:', funeralData.id, error);
        // Don't fail the funeral creation if contract creation fails
      }

      return result;
    }
    
    return baseSupabaseDataProvider.create(resource, params);
  },
  getQbInvoiceIds: (funeralId:string) => {    
    const resource = 'rpc/getqbinvoiceids';

    const params : GetListParams = {
      "filter": {"funeralid_input@": funeralId},
      "meta": undefined,
      "pagination": {"page": 1, "perPage": 25},
      "sort": {"field": "id", "order": "DESC"}, 
    };
  

    return baseSupabaseDataProvider.getList(resource, params);

    // return fetch(`${process.env.REACT_APP_API_URL}rest/v1/rpc/getQbInvoiceIds?funeralId_input=${funeralId}`, { method: 'GET' })
    // .then(response => response.json());
  }
};

export default pgrestDataProvider;
